import { Link } from "react-router-dom";

export default function WhatIsRoi() {
    return (
        <>
            <p>
                ROI — return on investment — is one of the most used and most misused terms in business and personal finance.
                The concept is simple: how much did you get back relative to what you put in? But getting the calculation right,
                and knowing when ROI is and isn't the right metric, matters more than most people realize.
            </p>

            <h2>The Basic ROI Formula</h2>
            <p>
                ROI = (Net Profit ÷ Cost of Investment) × 100
            </p>
            <p>
                Net profit is what you gained minus what you spent. Cost of investment is the total amount you put in.
            </p>
            <p>
                Example: You buy a rental property for $200,000. After one year, your rental income totals $18,000 and your
                expenses (mortgage interest, taxes, insurance, maintenance) total $12,000. Your net profit is $6,000.
            </p>
            <p>
                ROI = ($6,000 ÷ $200,000) × 100 = 3%
            </p>
            <p>
                That's the cash-on-cash return. If you factor in appreciation — say the property is now worth $210,000 —
                your net gain is $6,000 in income + $10,000 in appreciation = $16,000.
            </p>
            <p>
                ROI = ($16,000 ÷ $200,000) × 100 = 8%
            </p>
            <p>
                Use the <Link to="/calculator/investment-roi">investment ROI calculator</Link> to run these numbers for your
                specific investment without doing the algebra manually.
            </p>

            <h2>ROI When You Use Leverage</h2>
            <p>
                One of the most important adjustments: if you financed part of the investment, your ROI calculation changes
                depending on whether you use the full asset value or just your out-of-pocket cash.
            </p>
            <p>
                Back to the rental property. Say you put 20% down ($40,000) and financed $160,000. Your cash investment was
                $40,000. If your net cash flow after the mortgage payment is $2,400/year, and the property appreciated by
                $10,000:
            </p>
            <p>
                Cash-on-cash ROI = ($2,400 ÷ $40,000) × 100 = 6%<br />
                Total return ROI = ($12,400 ÷ $40,000) × 100 = 31%
            </p>
            <p>
                Leverage amplifies ROI significantly — in both directions. If the property loses value, leverage also amplifies
                your losses relative to your cash invested.
            </p>

            <h2>Time-Weighted ROI: The Number People Miss</h2>
            <p>
                A 20% ROI sounds great. But 20% over how long? A 20% return in 3 months is extraordinary. A 20% return over
                10 years is mediocre — roughly 1.8% annualized.
            </p>
            <p>
                To annualize an ROI, use: Annualized ROI = (1 + ROI)^(1/n) − 1, where n is the number of years.
            </p>
            <p>
                For a 50% total ROI over 4 years: (1 + 0.50)^(1/4) − 1 = 10.7% per year. That's a solid return — close to
                the S&P 500's long-run average. Always compare annualized returns, not raw returns, when evaluating
                investments across different time horizons.
            </p>

            <h2>What ROI Doesn't Tell You</h2>
            <p>
                ROI is a useful metric but has real limitations:
            </p>
            <p>
                <strong>It ignores risk.</strong> A 10% ROI from a savings account and a 10% ROI from a speculative startup
                investment are not equivalent. The startup has a much higher probability of total loss.
            </p>
            <p>
                <strong>It ignores time.</strong> As shown above, you have to annualize it to make comparisons meaningful.
            </p>
            <p>
                <strong>It ignores opportunity cost.</strong> If your investment returns 5% per year but the market returns 8%,
                your ROI looks positive but you're actually losing ground relative to what your money could have done.
            </p>
            <p>
                <strong>It doesn't account for taxes.</strong> Pre-tax and after-tax ROI can be very different. A rental
                property that generates $6,000 in profit might result in $4,500 after your marginal tax rate. An investment
                in a Roth IRA that generates the same gross return is worth more because withdrawals are tax-free.
            </p>

            <h2>ROI in Business Context</h2>
            <p>
                ROI is equally important in business investment decisions. If you spend $5,000 on a marketing campaign and
                generate $18,000 in gross sales with $10,000 in product costs, your profit from the campaign is $8,000.
            </p>
            <p>
                Marketing ROI = ($8,000 ÷ $5,000) × 100 = 160%
            </p>
            <p>
                That means every dollar spent on the campaign returned $2.60 in profit — an excellent result. Track this
                across channels and you can quickly identify which marketing efforts deserve more budget and which
                should be cut.
            </p>

            <h2>How to use ROI without misleading yourself</h2>
            <ul>
                <li>Always annualize when comparing investments with different time horizons.</li>
                <li>Compare against a benchmark (S&amp;P 500 for stocks, rent yield for property, etc.).</li>
                <li>Factor in taxes — pre-tax and after-tax ROI can differ by 30%+.</li>
                <li>Don't confuse "high ROI" with "good investment" — risk and time matter too.</li>
                <li>Use ROI alongside payback period and IRR for major decisions.</li>
            </ul>

            <p>
                Run your own scenarios — including leveraged and tax-adjusted returns — using our{" "}
                <Link to="/calculator/investment-roi">investment ROI calculator</Link> and the{" "}
                <Link to="/calculator/compound-interest">compound interest calculator</Link> to see the long-term effect.
            </p>
        </>
    );
}